import { Item } from "@/api"
import { Panel } from "primereact/panel"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { useMemo } from "react"
import { formatFloat } from "@/utils/format"

interface DateSummaryProps {
  selectedDate: string
  items: Item[]
  dates: string[]
  inventories: Record<Item["品目ID"], number[]>
  shipments: Record<Item["品目ID"], number[]>
  productions: Record<Item["品目ID"], number[]>
  holidayDates?: string[]
}

interface ProductionSummaryItem extends Item {
  生産数: number
  工程時間合計: number
}

interface ManufacturingSummaryItem {
  製造分類: string
  総工程時間: number
}

const DateSummary = ({
  selectedDate,
  items,
  dates,
  inventories,
  shipments,
  productions,
  holidayDates = []
}: DateSummaryProps) => {
  const selectedDateIndex = useMemo(() => dates.findIndex(date => date === selectedDate), [dates, selectedDate])

  const holidaySet = useMemo(() => new Set(holidayDates), [holidayDates])

  const previousProductionInfo = useMemo(() => {
    const emptyResult = { date: null as string | null, items: new Set<string>() }
    if (selectedDateIndex === -1) {
      return emptyResult
    }

    for (let idx = selectedDateIndex - 1; idx >= 0; idx -= 1) {
      if (holidaySet.has(dates[idx])) {
        continue
      }
      const producedIds = new Set<string>()
      items.forEach(item => {
        const productionValue = productions[item.品目ID]?.[idx] ?? 0
        if (productionValue > 0) {
          producedIds.add(item.品目ID)
        }
      })
      if (producedIds.size > 0) {
        return { date: dates[idx] ?? null, items: producedIds }
      }
    }

    return emptyResult
  }, [dates, holidaySet, items, productions, selectedDateIndex])

  const producedItems = useMemo(() => {
    if (selectedDateIndex === -1) return []

    const producedItemsList: ProductionSummaryItem[] = []
    items.forEach(item => {
      const productionData = productions[item.品目ID]
      if (productionData && productionData[selectedDateIndex] > 0) {
        const productionCount = productionData[selectedDateIndex]
        producedItemsList.push({
          ...item,
          生産数: productionCount,
          工程時間合計: (item.工程時間?.value ?? 0) * productionCount
        })
      }
    })

    return producedItemsList.sort((a, b) => b.生産数 - a.生産数)
  }, [items, productions, selectedDateIndex])

  const highlightedItemIds = useMemo(() => {
    return producedItems
      .filter(item => previousProductionInfo.items.has(item.品目ID))
      .map(item => item.品目ID)
  }, [producedItems, previousProductionInfo.items])

  const highlightActive = highlightedItemIds.length > 0

  const totalProduction = useMemo(() => producedItems.reduce((sum, item) => sum + item.生産数, 0), [producedItems])

  const categoryCount = useMemo(() => {
    const categories = new Set(producedItems.map(item => item.分類))
    return categories.size
  }, [producedItems])

  const totalProcessTime = useMemo(() => {
    return producedItems.reduce((sum, item) => sum + item.工程時間合計, 0)
  }, [producedItems])

  const manufacturingSummary = useMemo<ManufacturingSummaryItem[]>(() => {
    const summaryMap = new Map<string, number>()
    producedItems.forEach(item => {
      const key = item.製造分類 ?? '未分類'
      summaryMap.set(key, (summaryMap.get(key) ?? 0) + item.工程時間合計)
    })

    return Array.from(summaryMap.entries())
      .map(([分類, time]) => ({ 製造分類: 分類, 総工程時間: time }))
      .sort((a, b) => b.総工程時間 - a.総工程時間)
  }, [producedItems])

  return (
    <div className="flex flex-column gap-2">
      <h3>{selectedDate}のサマリ</h3>
      <Panel
        header={`この日に生産された品目 (合計: ${totalProduction}個, 分類数: ${categoryCount}, 総工程時間: ${formatFloat(totalProcessTime, 2)})`}
        className={`w-full ${highlightActive ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}
        toggleable
      >
        {highlightActive && (
          <div className="text-orange-600 text-sm font-semibold mb-3 flex gap-2 flex-wrap">
            <span>前回稼働日({previousProductionInfo.date ?? '不明'})に生産された品目:</span>
            <span className="font-mono text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
              {highlightedItemIds.join(', ')}
            </span>
          </div>
        )}
        {producedItems.length > 0 ? (
          <>
            <DataTable
              value={producedItems}
              size="small"
              stripedRows
              scrollable
              scrollHeight="400px"
              rowClassName={(rowData: ProductionSummaryItem) => ({
                'border-l-4 border-orange-400 bg-orange-50/70': previousProductionInfo.items.has(rowData.品目ID)
              })}
            >
              <Column field="品目ID" header="品目ID" sortable />
              <Column field="品目名" header="品目名" sortable />
              <Column field="分類" header="分類" sortable />
              <Column field="製造分類" header="製造分類" sortable />
              <Column
                field="生産数"
                header="生産数"
                body={(rowData: ProductionSummaryItem) => rowData.生産数}
                sortable
              />
              <Column
                field="工程時間"
                header="工程時間"
                body={(rowData: ProductionSummaryItem) => formatFloat(rowData.工程時間.value, 4)}
                sortable
              />
              <Column
                field="工程時間合計"
                header="工程時間合計"
                body={(rowData: ProductionSummaryItem) => formatFloat(rowData.工程時間合計, 2)}
                sortable
              />
            </DataTable>
            {manufacturingSummary.length > 0 && (
              <div className="mt-4">
                <h4>製造分類別 工程時間合計</h4>
                <DataTable value={manufacturingSummary} size="small" stripedRows>
                  <Column field="製造分類" header="製造分類" sortable />
                  <Column
                    field="総工程時間"
                    header="総工程時間"
                    body={(rowData: ManufacturingSummaryItem) => formatFloat(rowData.総工程時間, 2)}
                    sortable
                  />
                </DataTable>
              </div>
            )}
          </>
        ) : (
          <div className="text-center p-4 text-600">
            この日は生産された品目がありません
          </div>
        )}
      </Panel>
    </div>
  )
}

export default DateSummary
